import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, FileCheck2, Server, Database, Users2, ArrowRight } from "lucide-react";
import { PlatformIcon, PlatformWordmark } from "@/components/brand/PlatformWordmark";

const NAVY = "#0b192c";
const GOLD = "#c4a454";
const BORDER = "#e2e1dd";

export default function Trust() {
  return (
    <div className="min-h-screen" style={{ background: "#fdfcf8" }}>
      {/* Header */}
      <nav className="sticky top-0 z-50 border-b backdrop-blur-md"
           style={{ borderColor: BORDER, background: "rgba(253,252,248,0.92)" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
          <Link to="/" className="flex items-center gap-3">
            <PlatformIcon className="h-10 w-10" width={40} height={40} />
            <div>
              <PlatformWordmark className="h-6" />
              <div className="text-[9px] uppercase tracking-[0.2em] font-semibold mt-1" style={{ color: "#94a3b8" }}>
                Trust Center
              </div>
            </div>
          </Link>
          <Link to="/" className="text-xs uppercase tracking-widest font-semibold" style={{ color: NAVY }}>
            Back to home →
          </Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
            Trust · Security · Compliance
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 leading-tight" style={{ color: NAVY }}>
            Built to earn the trust of<br />institutional capital.
          </h1>
          <p className="text-lg text-[#334155] mt-6 leading-relaxed">
            AIFLedger is the fund-administration platform used by Corpzo to service SEBI-registered
            Alternative Investment Funds in India. Every design choice here — from encryption to
            audit trails — starts from a single premise: institutional LPs never accept a &quot;trust us.&quot;
          </p>
        </div>

        {/* Six-panel grid */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <TrustCard icon={<Lock size={22} />} title="Data protection"
                     items={[
                       "TLS 1.3 in transit; AES-256 at rest (MongoDB Atlas)",
                       "Field-level encryption for PAN, Aadhaar, bank details (roadmap Q3 2026)",
                       "Cookie consent + Google Consent Mode v2 (DPDP + GDPR)",
                       "Password hashing via bcrypt (cost 12)",
                     ]} />
          <TrustCard icon={<ShieldCheck size={22} />} title="Access control"
                     items={[
                       "Role-based access (13 roles, per-module CRUD grants)",
                       "TOTP-based 2FA available for every account",
                       "Session cookies HttpOnly + Secure + SameSite",
                       "Idle auto-logout after 30 minutes",
                       "Brute-force lockout after 5 failed attempts",
                     ]} />
          <TrustCard icon={<FileCheck2 size={22} />} title="Compliance"
                     items={[
                       "SEBI (AIF) Regulations 2012 — Category I / II / III aligned",
                       "DPDP Act 2023-ready (data minimisation, consent, purpose)",
                       "PMLA — investor screening and record-keeping (12+ years)",
                       "SOC 2 Type I — assessment in progress",
                       "Data residency: primary storage in India (Mumbai / Hyderabad)",
                     ]} />
          <TrustCard icon={<Database size={22} />} title="Audit & immutability"
                     items={[
                       "Every mutation is audit-logged (user, IP, before/after)",
                       "Immutable audit stream (append-only, retained ≥ 7 years)",
                       "Maker-checker workflows on money movements",
                       "Full document vault with SHA-256 hashing",
                     ]} />
          <TrustCard icon={<Server size={22} />} title="Availability"
                     items={[
                       "Target 99.9% uptime (measured monthly)",
                       "Nightly backups with 30-day retention",
                       "Point-in-time restore up to 24 hours",
                       "Disaster recovery: RPO ≤ 24h, RTO ≤ 4h",
                       "Status page: status.aifledger.com (launching soon)",
                     ]} />
          <TrustCard icon={<Users2 size={22} />} title="Vendor management"
                     items={[
                       "MongoDB Atlas (data) · Emergent (hosting) · Resend (email)",
                       "Sandbox.co.in (PAN verification) · PostHog EU (analytics)",
                       "Every subprocessor covered by an executed DPA",
                       "Zero data shared with third parties for marketing",
                     ]} />
        </div>

        {/* Contact block */}
        <div className="mt-16 rounded-2xl border p-8 lg:p-10"
             style={{ borderColor: BORDER, background: "white" }}>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                Security disclosures
              </div>
              <h3 className="font-serif text-2xl mt-2" style={{ color: NAVY }}>
                Responsible disclosure
              </h3>
              <p className="text-[13px] text-[#475569] mt-3 leading-relaxed">
                If you believe you have discovered a vulnerability in AIFLedger, please report it
                privately. We commit to acknowledging within 48 hours, an initial triage within 5
                business days, and a fix or accepted-risk statement within 30 days.
              </p>
              <a href="mailto:security@aifledger.com"
                 className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold"
                 style={{ color: NAVY }}>
                security@aifledger.com <ArrowRight size={14} />
              </a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                Data protection officer
              </div>
              <h3 className="font-serif text-2xl mt-2" style={{ color: NAVY }}>
                DPDP / GDPR requests
              </h3>
              <p className="text-[13px] text-[#475569] mt-3 leading-relaxed">
                Data-subject requests — access, erasure, portability, correction — are honoured
                within 30 days as required by the DPDP Act 2023. Enterprise customers can request a
                signed Data Processing Agreement (DPA).
              </p>
              <a href="mailto:dpo@aifledger.com"
                 className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold"
                 style={{ color: NAVY }}>
                dpo@aifledger.com <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 flex gap-3 text-[13px] text-[#475569] flex-wrap">
          <Link to="/privacy" className="underline hover:text-[#0b192c]">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="underline hover:text-[#0b192c]">Terms of Service</Link>
          <span>·</span>
          <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event("aif-open-cookie-prefs")); }}
             className="underline hover:text-[#0b192c]">
            Cookie preferences
          </a>
        </div>
      </main>

      <footer className="border-t mt-12" style={{ borderColor: BORDER, background: "white" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-[11px] text-[#94a3b8]">
          © {new Date().getFullYear()} Ultiwise Ventures Private Limited · POWERED BY CORPZO.
        </div>
      </footer>
    </div>
  );
}

function TrustCard({ icon, title, items }) {
  return (
    <div className="rounded-xl border p-6" style={{ borderColor: BORDER, background: "white" }}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ background: "#faf9f5", border: `1px solid ${BORDER}`, color: GOLD }}>
          {icon}
        </div>
        <h3 className="font-serif text-xl" style={{ color: NAVY }}>{title}</h3>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li key={it} className="text-[13px] leading-relaxed text-[#334155] flex gap-2">
            <span style={{ color: GOLD }}>▸</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
